import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { STATUSPAGE } from '@repo/ui';
import {
  AcademicManagementGroup,
  AcademicManagementInternshipLocation,
  AcademicManagementProfessional,
  AcademicManagementTask,
} from 'domain/models';
import { IAcademicOverviewFilters } from 'presentation/app/modules/Reports/models/academic-overview-filters';

export interface AcademicOverviewSectionState<T> {
  data: T[];
  total: number;
  page: number;
  length: number;
  search?: string;
  status: STATUSPAGE;
  loadedForFiltersKey?: string;
}

interface IAcademicOverviewState {
  filters: IAcademicOverviewFilters;
  groups: AcademicOverviewSectionState<AcademicManagementGroup>;
  tasks: AcademicOverviewSectionState<AcademicManagementTask>;
  internshipLocations: AcademicOverviewSectionState<AcademicManagementInternshipLocation>;
  professionals: AcademicOverviewSectionState<AcademicManagementProfessional>;
}

interface IPayloadAcademicOverviewFilters
  extends PayloadAction<IAcademicOverviewFilters> {}

const createInitialSectionState = <T>(): AcademicOverviewSectionState<T> => ({
  data: [],
  total: 0,
  page: 1,
  length: 10,
  status: STATUSPAGE.DONE,
});

const initialState: IAcademicOverviewState = {
  filters: null,
  groups: createInitialSectionState(),
  tasks: createInitialSectionState(),
  internshipLocations: createInitialSectionState(),
  professionals: createInitialSectionState(),
};

export const AcademicOverviewSlice = createSlice({
  name: 'academic-overview',
  initialState,
  reducers: {
    loadFilters: (state, action: IPayloadAcademicOverviewFilters) => {
      state.filters = action.payload;
    },
    loadGroups: (
      state,
      action: PayloadAction<
        AcademicOverviewSectionState<AcademicManagementGroup>
      >,
    ) => {
      state.groups = action.payload;
    },
    loadTasks: (
      state,
      action: PayloadAction<
        AcademicOverviewSectionState<AcademicManagementTask>
      >,
    ) => {
      state.tasks = action.payload;
    },
    loadInternshipLocations: (
      state,
      action: PayloadAction<
        AcademicOverviewSectionState<AcademicManagementInternshipLocation>
      >,
    ) => {
      state.internshipLocations = action.payload;
    },
    loadProfessionals: (
      state,
      action: PayloadAction<
        AcademicOverviewSectionState<AcademicManagementProfessional>
      >,
    ) => {
      state.professionals = action.payload;
    },
    clearData: (state) => {
      state.groups = createInitialSectionState();
      state.tasks = createInitialSectionState();
      state.internshipLocations = createInitialSectionState();
      state.professionals = createInitialSectionState();
    },
    clearFilters: (state) => {
      state.filters = null;
    },
  },
});

export const {
  loadFilters,
  loadGroups,
  loadTasks,
  loadInternshipLocations,
  loadProfessionals,
  clearData,
  clearFilters,
} = AcademicOverviewSlice.actions;

export default AcademicOverviewSlice.reducer;
